<script setup lang="ts">
import { useFlowEditorContext } from '../../composables/useFlowEditor'
import { isMediaAction, mediaTypeChoices } from '../../model/inspectorOptions'
import type { FlowNode } from '../../model/types'

/** Media type and display mode of show-media actions (including legacy show-* types). */
defineProps<{ selectedNode: FlowNode }>()

const editor = useFlowEditorContext()
const {
  updateMediaType,
  updateMediaTypeRight,
  updateDisplayMode: toggleDisplayMode,
  updateSplitSwap,
} = editor.inspector
</script>

<template>
  <!-- show-media sub-settings -->
  <template v-if="selectedNode.type === 'action' && isMediaAction(selectedNode.data.actionType)">
    <!-- 2x2 media type selector -->
    <div class="flow-media-type-grid">
      <button
        v-for="mt in mediaTypeChoices"
        :key="mt.value"
        type="button"
        :class="[
          'flow-media-type-btn',
          { 'flow-media-type-btn--active': (selectedNode.data.mediaType ?? 'image') === mt.value },
        ]"
        @click="updateMediaType(mt.value)"
      >
        <span class="flow-media-type-btn__icon">{{ mt.icon }}</span>
        <span>{{ mt.label }}</span>
      </button>
    </div>

    <!-- Display mode: 3 exclusive toggle buttons -->
    <div class="flow-display-mode-section">
      <span class="flow-settings__label">{{ $t('flow.displayMode') }}</span>
      <div class="flow-display-mode-btns">
        <button
          type="button"
          :class="[
            'flow-dm-btn',
            { 'flow-dm-btn--active': selectedNode.data.displayMode === 'standalone' },
          ]"
          :title="$t('flow.standaloneDesc')"
          @click="toggleDisplayMode('standalone')"
        >
          🖥 {{ $t('flow.standalone') }}
        </button>
        <button
          type="button"
          :class="[
            'flow-dm-btn',
            { 'flow-dm-btn--active': selectedNode.data.displayMode === 'modal' },
          ]"
          :title="$t('flow.modalDesc')"
          @click="toggleDisplayMode('modal')"
        >
          ◻ {{ $t('flow.modal') }}
        </button>
        <button
          type="button"
          :class="[
            'flow-dm-btn',
            { 'flow-dm-btn--active': selectedNode.data.displayMode === 'split' },
          ]"
          :title="$t('flow.splitDesc')"
          @click="toggleDisplayMode('split')"
        >
          ▥ {{ $t('flow.split') }}
        </button>
        <button
          type="button"
          :class="[
            'flow-dm-btn',
            {
              'flow-dm-btn--active':
                !selectedNode.data.displayMode || selectedNode.data.displayMode === 'panel',
            },
          ]"
          :title="$t('flow.displayModePanelHint')"
          @click="toggleDisplayMode('panel')"
        >
          ◳ {{ $t('flow.panel') }}
        </button>
      </div>
    </div>

    <!-- Split mode: second 2×2 grid for right-pane media type -->
    <template v-if="selectedNode.data.displayMode === 'split'">
      <div class="flow-settings__label">{{ $t('flow.splitRightMedia') }}</div>
      <div class="flow-media-type-grid">
        <button
          v-for="mt in mediaTypeChoices"
          :key="mt.value"
          type="button"
          :class="[
            'flow-media-type-btn',
            {
              'flow-media-type-btn--active':
                (selectedNode.data.mediaTypeRight ?? selectedNode.data.mediaType ?? 'image') ===
                mt.value,
            },
          ]"
          @click="updateMediaTypeRight(mt.value)"
        >
          <span class="flow-media-type-btn__icon">{{ mt.icon }}</span>
          <span>{{ mt.label }}</span>
        </button>
      </div>
      <label class="flow-settings__checkbox-label">
        <input
          type="checkbox"
          :checked="selectedNode.data.splitSwap === true"
          @change="updateSplitSwap(($event.target as HTMLInputElement).checked)"
        />
        <span>{{ $t('flow.splitSwap') }}</span>
      </label>
    </template>
  </template>
</template>

<style scoped src="../styles/inspectorForm.css"></style>
